/**
 * PATH: packages/job-hub/src/jobs/tabs/job-qa/useQaBulkSelection.ts
 * FILE_ID: JOBHUB-QA-BULK-SELECTION-001
 * PURPOSE: Selection state for the QA areas rail and the checklist item list.
 * WHY: jobs-list/jobsHubSelection.ts answers the same question for numeric job
 *      ids. QA ids are uuid strings, and widening that shared helper to a
 *      generic would touch every jobs-list caller for no gain here.
 * LANDMINE: selection is pruned against the CURRENT id list on every read.
 *      Counting is not membership: an id left over from a previous inspection
 *      would otherwise be posted in the next bulk assign.
 */
import { useCallback, useMemo, useState } from 'react'

export interface QaBulkSelection {
  /** Ids that are both selected and currently visible. */
  selected: string[]
  isSelected: (id: string) => boolean
  toggle: (id: string) => void
  selectAll: () => void
  selectIds: (nextIds: readonly string[]) => void
  deselectIds: (nextIds: readonly string[]) => void
  clear: () => void
  allSelected: boolean
  someSelected: boolean
  count: number
}

export function useQaBulkSelection(ids: readonly string[]): QaBulkSelection {
  const [raw, setRaw] = useState<ReadonlySet<string>>(() => new Set())

  const selected = useMemo(
    () => ids.filter((id) => raw.has(id)),
    [ids, raw],
  )

  const toggle = useCallback((id: string) => {
    setRaw((current) => {
      const next = new Set(current)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectAll = useCallback(() => setRaw(new Set(ids)), [ids])
  const selectIds = useCallback((nextIds: readonly string[]) => {
    setRaw((current) => {
      const next = new Set(current)
      for (const id of nextIds) next.add(id)
      return next
    })
  }, [])
  const deselectIds = useCallback((nextIds: readonly string[]) => {
    setRaw((current) => {
      const next = new Set(current)
      for (const id of nextIds) next.delete(id)
      return next
    })
  }, [])
  const clear = useCallback(() => setRaw(new Set()), [])

  const allSelected = ids.length > 0 && selected.length === ids.length
  return {
    selected,
    isSelected: (id: string) => raw.has(id),
    toggle,
    selectAll,
    selectIds,
    deselectIds,
    clear,
    allSelected,
    someSelected: selected.length > 0 && !allSelected,
    count: selected.length,
  }
}
